import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Settings, MapPin, Star, Sliders, Zap, Shield, Globe, Save, RotateCcw } from 'lucide-react';

const DEFAULT_CONFIG = {
  // Zones
  zones: ['Marrakech', 'Agadir'],
  active_zones: { marrakech: true, agadir: true },
  // Pricing
  base_price_marrakech: 1200,
  base_price_agadir: 900,
  coordination_fee_pct: 15,
  // Matching
  weight_accessibility: 40,
  weight_location: 30,
  weight_budget: 20,
  weight_dates: 10,
  min_quality_score: 3.5,
  // General
  response_delay_hours: 48,
  max_concurrent_stays: 20,
  booking_advance_days: 14,
  // Certification
  cert_min_score: 4.0,
  cert_renewal_months: 12,
};

const SECTION_CONFIGS = [
  {
    id: 'zones', label: 'Zones couvertes', Icon: MapPin, color: '#5A3B31',
    fields: [
      { key: 'base_price_marrakech', label: 'Prix de base Marrakech (EUR)', type: 'number', hint: 'Prix minimum d\'un séjour 7 jours' },
      { key: 'base_price_agadir', label: 'Prix de base Agadir (EUR)', type: 'number', hint: 'Prix minimum d\'un séjour 7 jours' },
      { key: 'coordination_fee_pct', label: 'Frais de coordination (%)', type: 'number', hint: 'Commission M-CareLink sur chaque séjour' },
    ]
  },
  {
    id: 'matching', label: 'Matching IA — Pondérations', Icon: Zap, color: '#F47B20',
    fields: [
      { key: 'weight_accessibility', label: 'Poids accessibilité (%)', type: 'number', hint: 'Doit totaliser 100% avec les autres pondérations' },
      { key: 'weight_location', label: 'Poids localisation (%)', type: 'number' },
      { key: 'weight_budget', label: 'Poids budget (%)', type: 'number' },
      { key: 'weight_dates', label: 'Poids disponibilités (%)', type: 'number' },
      { key: 'min_quality_score', label: 'Score qualité minimum partenaire', type: 'number', hint: 'Note minimale (sur 5) pour le matching automatique' },
    ]
  },
  {
    id: 'operations', label: 'Opérations', Icon: Sliders, color: '#8B5CF6',
    fields: [
      { key: 'response_delay_hours', label: 'Délai de réponse standard (heures)', type: 'number' },
      { key: 'max_concurrent_stays', label: 'Nombre max de séjours simultanés', type: 'number' },
      { key: 'booking_advance_days', label: 'Délai minimum de réservation (jours)', type: 'number' },
    ]
  },
  {
    id: 'certification', label: 'Certification partenaires', Icon: Shield, color: '#22863A',
    fields: [
      { key: 'cert_min_score', label: 'Note minimale pour certification', type: 'number', hint: 'Note sur 5 — en dessous, le partenaire perd la certification' },
      { key: 'cert_renewal_months', label: 'Renouvellement certification (mois)', type: 'number' },
    ]
  },
];

export default function SettingsModule() {
  const [config, setConfig] = useState({ ...DEFAULT_CONFIG });
  const [dirty, setDirty] = useState(false);
  const [openSection, setOpenSection] = useState('zones');

  const updateField = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const save = () => {
    // Validate matching weights
    const total = config.weight_accessibility + config.weight_location + config.weight_budget + config.weight_dates;
    if (total !== 100) {
      toast.error(`Les pondérations de matching doivent totaliser 100% (actuellement ${total}%)`);
      return;
    }
    setDirty(false);
    toast.success('Configuration sauvegardée avec succès');
  };

  const reset = () => {
    setConfig({ ...DEFAULT_CONFIG });
    setDirty(false);
    toast.success('Configuration réinitialisée');
  };

  return (
    <div className="p-6 space-y-6" data-testid="settings-module">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-semibold text-primary">Paramétrage</h2>
          <p className="text-sm text-muted-foreground font-body">Configuration générale de la plateforme M-CareLink</p>
        </div>
        <div className="flex gap-2">
          {dirty && (
            <Button variant="outline" onClick={reset} className="font-body rounded-full gap-1 text-sm" data-testid="settings-reset-btn">
              <RotateCcw className="w-3.5 h-3.5" /> Réinitialiser
            </Button>
          )}
          <Button onClick={save} className={`font-body rounded-full gap-1 text-sm ${dirty ? 'bg-accent hover:bg-accent/90 text-white' : 'bg-muted text-muted-foreground'}`} data-testid="settings-save-btn">
            <Save className="w-3.5 h-3.5" /> Sauvegarder
          </Button>
        </div>
      </div>

      {dirty && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl px-4 py-2.5 flex items-center gap-2">
          <Settings className="w-4 h-4 text-orange-600 flex-shrink-0" />
          <span className="text-sm font-body text-orange-800">Modifications non sauvegardées</span>
        </div>
      )}

      {/* Zones status */}
      <div className="grid grid-cols-2 gap-4">
        {[
          { key: 'marrakech', label: 'Marrakech', partners: 3 },
          { key: 'agadir', label: 'Agadir', partners: 3 },
        ].map(zone => (
          <div key={zone.key} className="bg-white rounded-2xl border border-border shadow-sm p-5" data-testid={`settings-zone-${zone.key}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-primary" />
                <span className="font-heading font-semibold text-primary">{zone.label}</span>
              </div>
              <Badge className={config.active_zones[zone.key] ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}>
                {config.active_zones[zone.key] ? 'Active' : 'Inactive'}
              </Badge>
            </div>
            <div className="text-xs text-muted-foreground font-body space-y-1">
              <p>{zone.partners} partenaires certifiés</p>
              <p>Base : {zone.key === 'marrakech' ? config.base_price_marrakech : config.base_price_agadir} EUR / 7 jours</p>
            </div>
            <button
              onClick={() => { setConfig(prev => ({ ...prev, active_zones: { ...prev.active_zones, [zone.key]: !prev.active_zones[zone.key] } })); setDirty(true); }}
              className={`mt-3 w-full py-1.5 rounded-full text-xs font-body font-medium transition-all ${config.active_zones[zone.key] ? 'bg-red-50 text-red-600 hover:bg-red-100' : 'bg-green-50 text-green-600 hover:bg-green-100'}`}
            >
              {config.active_zones[zone.key] ? 'Désactiver la zone' : 'Activer la zone'}
            </button>
          </div>
        ))}
      </div>

      {/* Sections accordion */}
      <div className="space-y-3">
        {SECTION_CONFIGS.map(section => {
          const Icon = section.Icon;
          const isOpen = openSection === section.id;
          return (
            <div key={section.id} className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden" data-testid={`settings-section-${section.id}`}>
              <button
                onClick={() => setOpenSection(isOpen ? null : section.id)}
                className="w-full flex items-center gap-3 px-5 py-4 hover:bg-background/50 transition-colors text-left"
              >
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: section.color + '18' }}>
                  <Icon className="w-4 h-4" style={{ color: section.color }} />
                </div>
                <span className="flex-1 font-heading font-semibold text-primary text-sm">{section.label}</span>
                <Globe className={`w-4 h-4 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
              </button>
              {isOpen && (
                <div className="px-5 pb-5 space-y-4 border-t border-border/50 pt-4">
                  {section.fields.map(field => (
                    <div key={field.key}>
                      <label className="text-xs font-body font-semibold text-muted-foreground mb-1 block">{field.label}</label>
                      <Input
                        type={field.type}
                        value={config[field.key]}
                        onChange={e => updateField(field.key, field.type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value)}
                        className="font-body text-sm max-w-xs"
                        data-testid={`settings-field-${field.key}`}
                      />
                      {field.hint && <p className="text-xs text-muted-foreground font-body mt-1">{field.hint}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* System info */}
      <div className="bg-background rounded-2xl border border-border p-5">
        <h3 className="font-heading font-semibold text-primary text-sm mb-3">Informations système</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Version', value: 'v1.0.0' },
            { label: 'Environnement', value: 'Production' },
            { label: 'Base de données', value: 'Supabase' },
            { label: 'Dernière mise à jour', value: 'Fév 2026' },
          ].map(({ label, value }) => (
            <div key={label} className="text-center">
              <p className="font-heading font-semibold text-primary text-sm">{value}</p>
              <p className="text-xs text-muted-foreground font-body">{label}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
